import { Alert, Button, Card, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Outcome = {
  id: string;
  code: string;
  statement: string;
  sequence_no: number;
  cognitive_level?: string | null;
};

type Detail = {
  unit: {
    id: string;
    code: string;
    name: string;
    description?: string | null;
    hours_hint?: number | null;
    is_active?: number | boolean;
    subject_code?: string;
    subject_name?: string;
    grade_code?: string;
    grade_name?: string;
  };
  outcomes: Outcome[];
};

export default function CurriculumUnitDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.curriculum.manage') || user?.permissions?.includes('education.admin')
  );

  const detail = useQuery({
    queryKey: ['education-curriculum-unit', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<Detail>(`/education/curriculum/units/${id}`);
      return res.data;
    },
  });

  const removeUnit = useMutation({
    mutationFn: () => api.delete(`/education/curriculum/units/${id}`),
    onSuccess: () => {
      message.success(t('curriculumUnitDeleted'));
      queryClient.invalidateQueries({ queryKey: ['education-curriculum-units'] });
      navigate('/education/curriculum/units');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const removeOutcome = useMutation({
    mutationFn: (outcomeId: string) => api.delete(`/education/curriculum/units/${id}/outcomes/${outcomeId}`),
    onSuccess: () => {
      message.success(t('curriculumOutcomeDeleted'));
      queryClient.invalidateQueries({ queryKey: ['education-curriculum-unit', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (detail.isError) {
    return (
      <EducationPageChrome title={t('curriculumUnits')}>
        <Alert type="error" showIcon message={t('unavailable', { status: 404 })} />
      </EducationPageChrome>
    );
  }

  const unit = detail.data?.unit;
  const inactive = unit?.is_active === 0 || unit?.is_active === false;

  return (
    <EducationPageChrome
      title={unit ? `${unit.code} — ${unit.name}` : t('curriculumUnits')}
      description={
        unit
          ? `${unit.subject_code || ''} · ${unit.grade_code || unit.grade_name || ''}`
          : t('curriculumUnitsIntro')
      }
      extra={
        <Space wrap>
          {canManage ? (
            <>
              <Button onClick={() => navigate(`/education/curriculum/units/${id}/edit`)}>{t('edit')}</Button>
              <Button type="primary" onClick={() => navigate(`/education/curriculum/units/${id}/outcomes/new`)}>
                {t('newOutcome')}
              </Button>
              <Popconfirm title={t('curriculumUnitDeleteConfirm')} onConfirm={() => removeUnit.mutate()}>
                <Button danger loading={removeUnit.isPending}>
                  {t('remove')}
                </Button>
              </Popconfirm>
            </>
          ) : null}
          <Button onClick={() => navigate('/education/curriculum/units')}>{t('backToUnits')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {inactive ? <Alert type="warning" showIcon message={t('curriculumUnitInactive')} /> : null}
        {unit?.description ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {unit.description}
          </Typography.Paragraph>
        ) : null}
        {unit?.hours_hint != null ? (
          <Typography.Text type="secondary">
            {t('hoursHint')}: {unit.hours_hint}
          </Typography.Text>
        ) : null}
        <Card size="small" title={t('outcomes')} loading={detail.isLoading}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={detail.data?.outcomes ?? []}
            locale={{ emptyText: t('curriculumOutcomesEmpty') }}
            columns={[
              { title: t('sequence'), dataIndex: 'sequence_no', width: 80 },
              { title: t('code'), dataIndex: 'code', width: 100 },
              { title: t('outcomeStatement'), dataIndex: 'statement' },
              {
                title: t('cognitiveLevel'),
                dataIndex: 'cognitive_level',
                width: 120,
                render: (v: string | null | undefined) => (v ? <Tag>{v}</Tag> : '—'),
              },
              ...(canManage
                ? [
                    {
                      title: t('actions'),
                      width: 160,
                      render: (_: unknown, row: Outcome) => (
                        <Space size="small">
                          <Button
                            size="small"
                            type="link"
                            onClick={() =>
                              navigate(`/education/curriculum/units/${id}/outcomes/${row.id}/edit`)
                            }
                          >
                            {t('edit')}
                          </Button>
                          <Popconfirm
                            title={t('curriculumOutcomeDeleteConfirm')}
                            onConfirm={() => removeOutcome.mutate(row.id)}
                          >
                            <Button size="small" type="link" danger>
                              {t('remove')}
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
